<template>
  <div
    v-if="show && banner"
    class="banner-popup-overlay"
    role="dialog"
    aria-modal="true"
    @click.self="dismiss"
  >
    <!-- box shrink-wraps the image so the close button always sits on the image
         corner, at any aspect ratio (portrait / landscape / square) -->
    <div class="banner-popup-box">
      <a
        v-if="banner.linkUrl"
        :href="banner.linkUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="banner-popup-link"
        @click="onLinkClick"
      >
        <img :src="banner.imageUrl" :alt="banner.altText || ''" class="banner-popup-img" />
      </a>
      <img v-else :src="banner.imageUrl" :alt="banner.altText || ''" class="banner-popup-img" />

      <button
        type="button"
        class="banner-popup-close"
        :aria-label="$t('cancel')"
        @click="dismiss"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path
            fill="currentColor"
            d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
          />
        </svg>
        <v-tooltip activator="parent" location="top">{{ $t('close') }}</v-tooltip>
      </button>
    </div>
  </div>
</template>

<script>
const VALID_PAGE_KEYS = ['login', 'product_admin', 'portal', 'fullsnippet', 'landing'];

export default {
  name: 'BannerPopup',

  data() {
    return {
      loaded: false,
      enabled: false,
      targetPages: [],
      banner: null,
      show: false,
      // Pages already shown during this app load (full page session). Reset on refresh
      // because the component remounts. Ensures the banner shows once per page per load
      // and does NOT re-pop when navigating back to a page within the SPA.
      shownPages: {},
    };
  },

  computed: {
    pageKey() {
      const route = this.$route || {};
      // Each page key maps to the FIRST/landing route of that area only, so the banner
      // shows on that page's initial load and not on every sub-page of the area.
      if (route.name === 'Login') return 'login';
      if (route.name === 'Landing') return 'landing'; // public home page
      if (route.name === 'dashboard') return 'portal'; // ChatFramework portal home (chat)
      if (route.name === 'SuperAdminDashboard') return 'product_admin'; // Product Admin home
      // Note: the 'fullsnippet' page is handled by the snippet loader
      // (public/Snippet/ChatFrameworkSnippetFullV2.js) on the host page, not here,
      // to avoid a duplicate popup inside the chat iframe.
      return null;
    },
  },

  // Re-evaluate when the route changes (SPA navigation). recompute() only shows the
  // banner the FIRST time each configured page is reached per app load, so navigating
  // away and back does not pop it again — only a refresh (remount) does.
  watch: {
    '$route.name'(newName, oldName) {
      if (newName !== oldName) this.recompute();
    },
  },

  mounted() {
    this.fetchCurrent();
  },

  methods: {
    fetchCurrent() {
      this.$call.WebContentBannerPopupCurrent(
        (res) => {
          this.loaded = true;
          if (res && res.status === 0 && res.data) {
            this.enabled = !!res.data.enabled;
            this.targetPages = Array.isArray(res.data.targetPages)
              ? res.data.targetPages.filter((p) => VALID_PAGE_KEYS.includes(p))
              : [];
            this.banner = res.data.banner || null;
          }
          this.recompute();
        },
        () => {
          this.loaded = true;
        }
      );
    },

    recompute() {
      const key = this.pageKey;
      const eligible =
        this.loaded &&
        this.enabled &&
        !!this.banner &&
        !!key &&
        this.targetPages.includes(key);

      if (eligible && !this.shownPages[key]) {
        this.shownPages[key] = true; // mark as shown for this app load
        this.show = true;
      } else {
        // Not eligible, or already shown earlier this load (navigated back) → keep hidden.
        this.show = false;
      }
    },

    dismiss() {
      // Hide for the current view only. Already marked shown for this load, so it won't
      // re-pop on SPA navigation; it reappears on a full refresh (remount resets state).
      this.show = false;
    },

    onLinkClick() {
      this.dismiss();
    },
  },
};
</script>

<style scoped>
.banner-popup-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  display: grid;
  place-items: center;
  background: rgba(0, 0, 0, 0.6);
  box-sizing: border-box;
}

.banner-popup-box {
  position: relative;
  display: block;
  max-width: 100%;
  max-height: 100%;
  line-height: 0;
}

.banner-popup-link {
  display: block;
  max-width: 100%;
  max-height: 100%;
}

.banner-popup-img {
  display: block;
  /* Cap against the viewport (minus a hardcoded edge gap), not parent percentages.
     Nested max-height:100% does not resolve when the box height is auto, so tall
     (portrait) images overflowed short screens. vw/vh are viewport-relative and
     always resolve, so the image fits on every screen and keeps the edge gap. */
  max-width: calc(100vw - 2 * clamp(20px, 5vmin, 56px));
  max-height: calc(100vh - 2 * clamp(20px, 5vmin, 56px));
  border-radius: 8px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.4);
}

.banner-popup-close {
  position: absolute;
  top: -14px;
  right: -14px;
  z-index: 1;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: #ffffff;
  color: #333;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  transition: transform 0.15s ease, background 0.15s ease;
}

.banner-popup-close:hover {
  background: #f0f0f0;
  transform: scale(1.08);
}
</style>
